import { AiEntityType } from './aiEntities';

export type OpenCodeSession = {
    id: string;
    createdAt: Date;
};

export type OpenCodeSessionInfo = {
    id: string;
    title?: string;
    createdAt?: string;
    parentID?: string;
};

export enum OpenCodeRole {
    USER = 'user',
    ASSISTANT = 'assistant',
    SYSTEM = 'system',
}

export enum OpenCodeChatEvent {
    MESSAGE_PART = 'message_part',
    STEP_START = 'step_start',
    STEP_FINISH = 'step_finish',
    SUBAGENT_START = 'subagent_start',
    SUBAGENT_PART = 'subagent_part',
    SUBAGENT_COMPLETED = 'subagent_completed',
    SESSION_CREATED = 'session_created',
}

// Error Types
export type OpenCodeApiError = {
    name: 'APIError';
    data: {
        message: string;
        statusCode?: number;
        isRetryable: boolean;
        responseHeaders?: Record<string, string>;
        responseBody?: string;
    };
};

export type OpenCodeProviderAuthError = {
    name: 'ProviderAuthError';
    data: { providerID: string; message: string };
};

export type OpenCodeUnknownError = {
    name: 'UnknownError';
    data: { message: string };
};

export type OpenCodeMessageOutputLengthError = {
    name: 'MessageOutputLengthError';
    data: Record<string, unknown>;
};

export type OpenCodeMessageAbortedError = {
    name: 'MessageAbortedError';
    data: { message: string };
};

export type OpenCodeRefusalError = {
    name: 'RefusalError';
    data: { message: string };
};

export type OpenCodeError =
    | OpenCodeApiError
    | OpenCodeProviderAuthError
    | OpenCodeUnknownError
    | OpenCodeMessageOutputLengthError
    | OpenCodeMessageAbortedError
    | OpenCodeRefusalError;

export type OpenCodeFileDiff = {
    file: string;
    before: string;
    after: string;
    additions: number;
    deletions: number;
};

export type OpenCodeMessageTime = {
    created: number;
    completed?: number;
};

export type OpenCodeModelInfo = {
    providerID: string;
    modelID: string;
};

export type OpenCodeTokenUsage = {
    input: number;
    output: number;
    reasoning: number;
    cache: {
        read: number;
        write: number;
    };
};

export type OpenCodeCost = number;

export type OpenCodeFilePartSourceText = {
    value: string;
    start: number;
    end: number;
};

export type OpenCodeFileSource = {
    text: OpenCodeFilePartSourceText;
    type: 'file';
    path: string;
};

export type OpenCodeRange = {
    start: { line: number; character: number };
    end: { line: number; character: number };
};

export type OpenCodeSymbolSource = {
    text: OpenCodeFilePartSourceText;
    type: 'symbol';
    path: string;
    range: OpenCodeRange;
    name: string;
    kind: number;
};

export type OpenCodeFilePartSource = OpenCodeFileSource | OpenCodeSymbolSource;

/** Status of an OpenCode tool call lifecycle */
export enum OpenCodeToolStatus {
    PENDING = 'pending',
    RUNNING = 'running',
    COMPLETED = 'completed',
    ERROR = 'error',
}

export type OpenCodeToolStatePending = {
    status: OpenCodeToolStatus.PENDING;
    input: Record<string, unknown>;
    raw: string;
};

export type OpenCodeToolStateRunning = {
    status: OpenCodeToolStatus.RUNNING;
    input: Record<string, unknown>;
    title?: string;
    metadata?: Record<string, unknown>;
    time: { start: number };
};

export type OpenCodeToolStateCompleted = {
    status: OpenCodeToolStatus.COMPLETED;
    input: Record<string, unknown>;
    output: string;
    title: string;
    metadata: Record<string, unknown>;
    time: { start: number; end: number; compacted?: number };
    attachments?: Array<OpenCodeFilePart>;
};

export type OpenCodeToolStateError = {
    status: OpenCodeToolStatus.ERROR;
    input: Record<string, unknown>;
    error: string;
    metadata?: Record<string, unknown>;
    time: { start: number; end: number };
};

export type OpenCodeToolState =
    | OpenCodeToolStatePending
    | OpenCodeToolStateRunning
    | OpenCodeToolStateCompleted
    | OpenCodeToolStateError;

export type OpenCodeTextPart = {
    id: string;
    sessionID: string;
    messageID: string;
    type: 'text';
    text: string;
    synthetic?: boolean;
    ignored?: boolean;
    time?: { start: number; end?: number };
    metadata?: Record<string, unknown>;
};

export type OpenCodeReasoningPart = {
    id: string;
    sessionID: string;
    messageID: string;
    type: 'reasoning';
    text: string;
    metadata?: Record<string, unknown>;
    time: { start: number; end?: number };
};

export type OpenCodeFilePart = {
    id: string;
    sessionID: string;
    messageID: string;
    type: 'file';
    mime: string;
    filename?: string;
    url: string;
    source?: OpenCodeFilePartSource;
};

export type OpenCodeToolPart = {
    id: string;
    sessionID: string;
    messageID: string;
    type: 'tool';
    callID: string;
    tool: string;
    state: OpenCodeToolState;
    metadata?: Record<string, unknown>;
};

export type OpenCodeStepStartPart = {
    id: string;
    sessionID: string;
    messageID: string;
    type: 'step-start';
    snapshot?: string;
};

export type OpenCodeStepFinishPart = {
    id: string;
    sessionID: string;
    messageID: string;
    type: 'step-finish';
    reason: string;
    snapshot?: string;
    cost: OpenCodeCost;
    tokens: OpenCodeTokenUsage;
};

export type OpenCodeSnapshotPart = {
    id: string;
    sessionID: string;
    messageID: string;
    type: 'snapshot';
    snapshot: string;
};

export type OpenCodePatchPart = {
    id: string;
    sessionID: string;
    messageID: string;
    type: 'patch';
    hash: string;
    files: Array<string>;
};

export type OpenCodeAgentPart = {
    id: string;
    sessionID: string;
    messageID: string;
    type: 'agent';
    name: string;
    source?: { value: string; start: number; end: number };
};

export type OpenCodeRetryPart = {
    id: string;
    sessionID: string;
    messageID: string;
    type: 'retry';
    attempt: number;
    error: OpenCodeApiError;
    time: { created: number };
};

export type OpenCodeCompactionPart = {
    id: string;
    sessionID: string;
    messageID: string;
    type: 'compaction';
    auto: boolean;
};

export type OpenCodeSubtaskPart = {
    id: string;
    sessionID: string;
    messageID: string;
    type: 'subtask';
    prompt: string;
    description: string;
    agent: string;
};

export type OpenCodeMessagePart =
    | OpenCodeTextPart
    | OpenCodeReasoningPart
    | OpenCodeFilePart
    | OpenCodeToolPart
    | OpenCodeStepStartPart
    | OpenCodeStepFinishPart
    | OpenCodeSnapshotPart
    | OpenCodePatchPart
    | OpenCodeAgentPart
    | OpenCodeRetryPart
    | OpenCodeCompactionPart
    | OpenCodeSubtaskPart;

export type OpenCodeUserMessageInfo = {
    id: string;
    sessionID: string;
    role: OpenCodeRole.USER;
    time: { created: number };
    summary?: {
        title?: string;
        body?: string;
        diffs: Array<OpenCodeFileDiff>;
    };
    agent: string;
    model: OpenCodeModelInfo;
    system?: string;
    tools?: Record<string, boolean>;
};

export type OpenCodeAssistantMessageInfo = {
    id: string;
    sessionID: string;
    role: OpenCodeRole.ASSISTANT;
    time: { created: number; completed?: number };
    error?: OpenCodeError;
    parentID: string;
    modelID: string;
    providerID: string;
    mode: string;
    path: { cwd: string; root: string };
    summary?: boolean;
    cost: OpenCodeCost;
    tokens: OpenCodeTokenUsage;
    finish?: string;
    agent: string;
};

export type OpenCodeMessageInfo =
    | OpenCodeUserMessageInfo
    | OpenCodeAssistantMessageInfo;

export type OpenCodeMessage = {
    info: OpenCodeMessageInfo;
    parts: OpenCodeMessagePart[];
};

export type OpenCodeHealth = {
    healthy: boolean;
    version?: string;
};

export type OpenCodeMcpContext = {
    projectUuid: string;
    token: string;
    tokenUuid: string;
    tokenExpiresAt: string;
    userUuid: string;
    taskId?: string;
    insightId?: string;
    questId?: string;
};

export type OpenCodeStreamingChunk = {
    part: OpenCodeMessagePart;
    delta?: string;
};

export type OpenCodeSubagentStreamingChunk = OpenCodeStreamingChunk & {
    subagentId: string;
};

export type OpenCodeMessagePartEvent = {
    event: 'message_part';
    data: OpenCodeStreamingChunk;
};

export type OpenCodeStepStartEvent = {
    event: 'step_start';
    data: OpenCodeStreamingChunk;
};

export type OpenCodeStepFinishEvent = {
    event: 'step_finish';
    data: OpenCodeStreamingChunk;
};

export type OpenCodeSubagentStartEvent = {
    event: 'subagent_start';
    data: OpenCodeSubagentStreamingChunk;
};

export type OpenCodeSubagentPartEvent = {
    event: 'subagent_part';
    data: OpenCodeSubagentStreamingChunk;
};

export type OpenCodeSubagentCompletedEvent = {
    event: 'subagent_completed';
    data: OpenCodeSubagentStreamingChunk;
};

export type OpenCodeDoneEvent = {
    event: 'done';
    data: Record<string, never>;
};

export type OpenCodeErrorEvent = {
    event: 'error';
    data: {
        error: string;
    };
};

export type OpenCodeSSEEvent =
    | OpenCodeMessagePartEvent
    | OpenCodeStepStartEvent
    | OpenCodeStepFinishEvent
    | OpenCodeSubagentStartEvent
    | OpenCodeSubagentPartEvent
    | OpenCodeSubagentCompletedEvent
    | OpenCodeDoneEvent
    | OpenCodeErrorEvent;

export enum OpenCodeToolActionType {
    NONE = 'none',
    REDIRECT = 'redirect',
    ENTITY_MUTATION = 'entity_mutation',
}

export enum OpenCodeEntityMutationOperation {
    CREATE = 'create',
    UPDATE = 'update',
}

export type OpenCodeToolRedirectAction = {
    type: OpenCodeToolActionType.REDIRECT;
    getPath: (projectUuid: string, output: Record<string, unknown>) => string;
};

export type OpenCodeToolEntityMutationAction = {
    type: OpenCodeToolActionType.ENTITY_MUTATION;
    entityType: AiEntityType;
    operation: OpenCodeEntityMutationOperation;
    getEntityId: (output: Record<string, unknown>) => string | undefined;
    getEntityRevision?: (output: Record<string, unknown>) => string | undefined;
};

export type OpenCodeToolAction =
    | { type: OpenCodeToolActionType.NONE }
    | OpenCodeToolRedirectAction
    | OpenCodeToolEntityMutationAction;

export type OpenCodeToolDisplayConfig = {
    runningLabel: string;
    completedLabel: string;
    errorLabel: string;
    action: OpenCodeToolAction;
};

export enum OpenCodeToolName {
    GET_AUDIENCE = 'sortment_get_audience',
    GET_AUDIENCES = 'sortment_get_audiences',
    GET_AUDIENCE_INSIGHTS = 'sortment_get_audience_insights',
    GET_CAMPAIGNS = 'sortment_get_campaigns',
    GET_EXTENDED_CAMPAIGN = 'sortment_get_extended_campaign',
    GET_CAMPAIGN_RUNS = 'sortment_get_campaign_runs',
    PUBLISH_CAMPAIGN = 'sortment_publish_campaign',
    UNPUBLISH_CAMPAIGN = 'sortment_unpublish_campaign',
    DELETE_CAMPAIGN = 'sortment_delete_campaign',
    TRIGGER_CAMPAIGN = 'sortment_trigger_campaign',
    CANCEL_CAMPAIGN = 'sortment_cancel_campaign',
    SEND_TEST_CAMPAIGN = 'sortment_send_test_campaign',
    GET_CAMPAIGN_STATUS = 'sortment_get_campaign_status',
    TRIGGER_INTERNAL_CAMPAIGN_QA = 'sortment_trigger_internal_campaign_qa',
    GET_INTERNAL_CAMPAIGN_QA = 'sortment_get_internal_campaign_qa',
    SAVE_INTERNAL_CAMPAIGN_QA_REPORT = 'sortment_save_internal_campaign_qa_report',
    DUPLICATE_CAMPAIGN = 'sortment_duplicate_campaign',
    GET_RELATION = 'sortment_get_relation',
    GET_PROJECT = 'sortment_get_project',
    GET_PROJECTS = 'sortment_get_projects',
    PREVIEW_AUDIENCE = 'sortment_preview_audience',
    CREATE_AUDIENCE = 'sortment_create_audience',
    UPDATE_AUDIENCE = 'sortment_update_audience',
    SCHEDULE_AUDIENCE = 'sortment_schedule_audience',
    UPDATE_AUDIENCE_SCHEDULE = 'sortment_update_audience_schedule',
    DELETE_AUDIENCE_SCHEDULE = 'sortment_delete_audience_schedule',
    ACTIVATE_AUDIENCE = 'sortment_activate_audience',
    DEACTIVATE_AUDIENCE = 'sortment_deactivate_audience',
    ARCHIVE_AUDIENCE = 'sortment_archive_audience',
    RESTORE_AUDIENCE = 'sortment_restore_audience',
    CACHE_AUDIENCE = 'sortment_cache_audience',
    MATERIALIZE_AUDIENCE = 'sortment_materialize_audience',
    EXPORT_AUDIENCE = 'sortment_export_audience',
    EXPORT_AUDIENCE_SYNC = 'sortment_export_audience_sync',
    COUNT_AUDIENCE = 'sortment_count_audience',
    RUN_SQL = 'sortment_run_sql',
    BEEFREE_TOOL = 'sortment_beefree-tool',
    WEB_FETCH_SAFE = 'sortment_web-fetch-safe',
    GET_SRT_TEMPLATE = 'sortment_get-srt-template',
    GET_BEEFREE_TEMPLATE = 'sortment_get-beefree-template',
    SAVE_AGENT_TASK_INSIGHT = 'sortment_save_agent_task_insight',
    GET_INTELLIGENCE_BLOCKS = 'sortment_get_intelligence_blocks',
    CREATE_INTELLIGENCE_BLOCK = 'sortment_create_intelligence_block',
    UPDATE_INTELLIGENCE_BLOCK = 'sortment_update_intelligence_block',
    READ_MEMORY = 'sortment_read_memory',
    ADD_TO_MEMORY = 'sortment_add_to_memory',
    GET_CUSTOM_ATTRIBUTES = 'sortment_get_custom_attributes',
    GET_CUSTOM_ATTRIBUTE_INSIGHTS = 'sortment_get_custom_attribute_insights',
    PREVIEW_CUSTOM_ATTRIBUTE = 'sortment_preview_custom_attribute',
    CREATE_CUSTOM_ATTRIBUTE = 'sortment_create_custom_attribute',
    UPDATE_CUSTOM_ATTRIBUTE = 'sortment_update_custom_attribute',
    DELETE_CUSTOM_ATTRIBUTE = 'sortment_delete_custom_attribute',
    TOGGLE_CUSTOM_ATTRIBUTE_MATERIALIZATION = 'sortment_toggle_custom_attribute_materialization',
    GET_KPIS = 'sortment_get_kpis',
    GET_KPI = 'sortment_get_kpi',
    PREVIEW_KPI = 'sortment_preview_kpi',
    CREATE_KPI = 'sortment_create_kpi',
    UPDATE_KPI = 'sortment_update_kpi',
    GET_KPI_DATA = 'sortment_get_kpi_data',
    GET_KPI_ANALYTICS = 'sortment_get_kpi_analytics',
    RUN_KPI_MATERIALIZATION = 'sortment_run_kpi_materialization',
    GET_ANALYTICS_ARTIFACTS = 'sortment_get_analytics_artifacts',
    GET_ANALYTICS_ARTIFACT = 'sortment_get_analytics_artifact',
    GET_SPACES = 'sortment_get_spaces',
    CREATE_SPACE = 'sortment_create_space',
    GET_DASHBOARDS = 'sortment_get_dashboards',
    GET_DASHBOARD = 'sortment_get_dashboard',
    CREATE_CHART = 'sortment_create_chart',
    CREATE_DASHBOARD = 'sortment_create_dashboard',
    ADD_CHARTS_TO_DASHBOARD = 'sortment_add_charts_to_dashboard',
    UPDATE_CHART = 'sortment_update_chart',
    GET_INTERNAL_EVENT_TABLES = 'sortment_get_internal_event_tables',
    UPDATE_RELATION_TABLE_LABEL = 'sortment_update_relation_table_label',
    UPDATE_RELATION_TABLE_DESCRIPTION = 'sortment_update_relation_table_description',
    UPDATE_RELATION_COLUMN_DISPLAY_NAME = 'sortment_update_relation_column_display_name',
    UPDATE_RELATION_COLUMN_DESCRIPTION = 'sortment_update_relation_column_description',
    UPDATE_RELATION_COLUMN_AI_USAGE_CONTEXT = 'sortment_update_relation_column_ai_usage_context',
    UPDATE_RELATION_COLUMN_SYNONYMS = 'sortment_update_relation_column_synonyms',
    GET_JOURNEYS = 'sortment_get_journeys',
    GET_JOURNEY = 'sortment_get_journey',
    GET_JOURNEY_EVENTS = 'sortment_get_journey_events',
    GET_JOURNEY_NODE_PARAMS = 'sortment_get_journey_node_params',
    CREATE_JOURNEY = 'sortment_create_journey',
    UPDATE_JOURNEY = 'sortment_update_journey',
    ADD_JOURNEY_NODE = 'sortment_add_journey_node',
    ADD_SEND_MESSAGE_NODE = 'sortment_add_send_message_node',
    CREATE_CAMPAIGN = 'sortment_create_campaign',
    VALIDATE_CAMPAIGN = 'sortment_validate_campaign',
    UPDATE_CAMPAIGN = 'sortment_update_campaign',
    SET_CAMPAIGN_SEND_TO = 'sortment_set_campaign_send_to',
    SET_CAMPAIGN_SENDER_DETAILS = 'sortment_set_campaign_sender_details',
    SET_CAMPAIGN_COMMUNICATION_DETAILS = 'sortment_set_campaign_communication_details',
    SET_CAMPAIGN_SUBSCRIPTION_GROUP = 'sortment_set_campaign_subscription_group',
    SET_CAMPAIGN_AUDIENCE = 'sortment_set_campaign_audience',
    SET_CAMPAIGN_CONTENT_MAPPINGS = 'sortment_set_campaign_content_mappings',
    SET_CAMPAIGN_TRACKING_PARAMETERS = 'sortment_set_campaign_tracking_parameters',
    SET_CAMPAIGN_PREVIEW_TEXT = 'sortment_set_campaign_preview_text',
    SET_CAMPAIGN_DELIVERY_CONTROLS = 'sortment_set_campaign_delivery_controls',
    SET_CAMPAIGN_CONVERSION_CONFIG = 'sortment_set_campaign_conversion_config',
    GET_SENDER_PROFILES = 'sortment_get_sender_profiles',
    GET_SUBSCRIPTION_GROUPS = 'sortment_get_subscription_groups',
    GET_TEMPLATES = 'sortment_get_templates',
    GET_TEMPLATES_FROM_CAMPAIGNS = 'sortment_get_templates_from_campaigns',
    GET_TEMPLATE_DETAILS = 'sortment_get_template_details',
    GET_TEMPLATE_TYPE = 'sortment_get_template_type',
    GET_TEMPLATE_THUMBNAIL = 'sortment_get_template_thumbnail',
    ESTIMATE_CAMPAIGN_REACH = 'sortment_estimate_campaign_reach',
    GET_INTEGRATIONS = 'sortment_get_integrations',
    RANDOM_ID_GENERATOR = 'sortment_random_id_generator',
    ATTACH_TEMPLATE_TO_CAMPAIGN_VARIANT = 'sortment_attach_template_to_campaign_variant',
    GET_TABLE = 'sortment_get_table',
    GET_RELATION_ID = 'sortment_get_relation_id',
    GET_USER_SUBSCRIPTION_PREFERENCES = 'sortment_get_user_subscription_preferences',
    UPDATE_USER_SUBSCRIPTION_PREFERENCES = 'sortment_update_user_subscription_preferences',
    GET_AGENT_TASK = 'sortment_get_agent_task',
    GET_AGENT_TASK_LIST = 'sortment_get_agent_task_list',
    CREATE_AGENT_TASK = 'sortment_create_agent_task',
    UPDATE_AGENT_TASK = 'sortment_update_agent_task',
    ACTIVATE_AGENT_TASK = 'sortment_activate_agent_task',
    DEACTIVATE_AGENT_TASK = 'sortment_deactivate_agent_task',
    DELETE_AGENT_TASK = 'sortment_delete_agent_task',
    RUN_AGENT_TASK_NOW = 'sortment_run_agent_task_now',
    GET_AGENT_TASK_INSIGHTS = 'sortment_get_agent_task_insights',
    GET_AGENT_TASK_INSIGHT = 'sortment_get_agent_task_insight',
    UPDATE_AGENT_TASK_INSIGHT = 'sortment_update_agent_task_insight',
    LIST_QUESTS = 'sortment_list_quests',
    CREATE_QUEST = 'sortment_create_quest',
    GET_QUEST = 'sortment_get_quest',
    GET_QUEST_TASKS = 'sortment_get_quest_tasks',
    GET_QUEST_ACTIVE_WAIT = 'sortment_get_quest_active_wait',
    GET_QUEST_HANDOFFS = 'sortment_get_quest_handoffs',
    STEER_QUEST = 'sortment_steer_quest',
    START_QUEST_CHAT = 'sortment_start_quest_chat',
    ADD_QUEST_PLAN_ITEM = 'sortment_add_quest_plan_item',
    UPDATE_QUEST_PLAN_ITEM = 'sortment_update_quest_plan_item',
    MOVE_QUEST_PLAN_ITEM = 'sortment_move_quest_plan_item',
    DELETE_QUEST_PLAN_ITEM = 'sortment_delete_quest_plan_item',
    COMPLETE_QUEST = 'sortment_complete_quest',
    CREATE_QUEST_HUMAN_INPUT = 'sortment_create_quest_human_input',
    CANCEL_QUEST_HUMAN_INPUT = 'sortment_cancel_quest_human_input',
    SUPERSEDE_QUEST_HUMAN_INPUT = 'sortment_supersede_quest_human_input',
    GET_QUEST_HUMAN_INPUTS = 'sortment_get_quest_human_inputs',
    APPEND_QUEST_ACTIVITY_LOG = 'sortment_append_quest_activity_log',
    GET_QUEST_ACTIVITY_LOG = 'sortment_get_quest_activity_log',
    SEND_QUEST_HANDOFF = 'sortment_send_quest_handoff',
    WAIT_UNTIL_QUEST_TASKS = 'sortment_wait_until_quest_tasks',
    UPDATE_QUEST_REPORT = 'sortment_update_quest_report',
    WEB_SEARCH = 'sortment_web_search',
    GET_HOME_FEED_ITEMS = 'sortment_get_home_feed_items',
    CREATE_HOME_FEED_ITEM = 'sortment_create_home_feed_item',
    GET_HOME_FEED_ITEM = 'sortment_get_home_feed_item',
    UPDATE_HOME_FEED_ITEM = 'sortment_update_home_feed_item',
    MARK_HOME_FEED_ITEM_READ = 'sortment_mark_home_feed_item_read',
    DELETE_HOME_FEED_ITEM = 'sortment_delete_home_feed_item',
    CREATE_ENTITY_TAG = 'sortment_create_entity_tag',
    CREATE_CHART_TAG = 'sortment_create_chart_tag',
    CREATE_SQL_TAG = 'sortment_create_sql_tag',
    CONNECTOR_API_REQUEST = 'sortment_connector_api_request',
    CREATE_CUSTOM_ATTRIBUTE_SYNC = 'sortment_create_custom_attribute_sync',
    CREATE_AUDIENCE_DESTINATION_SYNC = 'sortment_create_audience_destination_sync',
    BEEFREE_V2_CREATE_TEMPLATE = 'beefree-v2_beefree_v2_create_template',
    BEEFREE_V2_GET_TEMPLATE = 'beefree-v2_beefree_v2_get_template',
    CREATE_PUSH_TEMPLATE = 'sortment_create_push_template',
    READ_ATTACHMENT = 'sortment_read_attachment',
    GET_PUBLIC_ATTACHMENT_URL = 'sortment_get_public_attachment_url',
    UPLOAD_FILE_TO_RELATION = 'sortment_upload_file_to_relation',
    UPSERT_TABLE_FILE_TO_RELATION = 'sortment_upsert_table_file_to_relation',
    GET_FILE_UPLOAD_STATS = 'sortment_get_file_upload_stats',
}

const NONE_ACTION: OpenCodeToolAction = {
    type: OpenCodeToolActionType.NONE,
};

const getEntityId = (output: Record<string, unknown>): string | undefined =>
    typeof output.id === 'string' ? output.id : undefined;

const getJourneyEntityId = (
    output: Record<string, unknown>,
): string | undefined => {
    const journey =
        typeof output.journey === 'object' && output.journey !== null
            ? (output.journey as Record<string, unknown>)
            : undefined;
    return typeof journey?.id === 'string' ? journey.id : undefined;
};

const getEntityRevision = (
    output: Record<string, unknown>,
): string | undefined =>
    typeof output.updatedAt === 'string' ? output.updatedAt : undefined;

export const OPEN_CODE_TOOL_DISPLAY_CONFIG: Record<
    string,
    OpenCodeToolDisplayConfig
> = {
    [OpenCodeToolName.RUN_SQL]: {
        runningLabel: 'Running SQL query...',
        completedLabel: 'SQL query completed',
        errorLabel: 'SQL query failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CREATE_AUDIENCE]: {
        runningLabel: 'Creating audience...',
        completedLabel: 'Audience created',
        errorLabel: 'Audience creation failed',
        action: {
            type: OpenCodeToolActionType.ENTITY_MUTATION,
            entityType: AiEntityType.AUDIENCE,
            operation: OpenCodeEntityMutationOperation.CREATE,
            getEntityId,
            getEntityRevision,
        },
    },
    [OpenCodeToolName.UPDATE_AUDIENCE]: {
        runningLabel: 'Updating audience...',
        completedLabel: 'Audience updated',
        errorLabel: 'Audience update failed',
        action: {
            type: OpenCodeToolActionType.ENTITY_MUTATION,
            entityType: AiEntityType.AUDIENCE,
            operation: OpenCodeEntityMutationOperation.UPDATE,
            getEntityId,
            getEntityRevision,
        },
    },
    [OpenCodeToolName.PREVIEW_AUDIENCE]: {
        runningLabel: 'Previewing audience...',
        completedLabel: 'Audience preview ready',
        errorLabel: 'Audience preview failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_AUDIENCE]: {
        runningLabel: 'Fetching audience...',
        completedLabel: 'Audience loaded',
        errorLabel: 'Failed to fetch audience',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_AUDIENCES]: {
        runningLabel: 'Fetching audiences...',
        completedLabel: 'Audiences loaded',
        errorLabel: 'Failed to fetch audiences',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_AUDIENCE_INSIGHTS]: {
        runningLabel: 'Checking audience insights...',
        completedLabel: 'Audience insights ready',
        errorLabel: 'Audience insight check failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.SCHEDULE_AUDIENCE]: {
        runningLabel: 'Scheduling audience...',
        completedLabel: 'Audience scheduled',
        errorLabel: 'Audience scheduling failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UPDATE_AUDIENCE_SCHEDULE]: {
        runningLabel: 'Updating audience schedule...',
        completedLabel: 'Audience schedule updated',
        errorLabel: 'Audience schedule update failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.DELETE_AUDIENCE_SCHEDULE]: {
        runningLabel: 'Deleting audience schedule...',
        completedLabel: 'Audience schedule deleted',
        errorLabel: 'Audience schedule deletion failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.ACTIVATE_AUDIENCE]: {
        runningLabel: 'Activating audience...',
        completedLabel: 'Audience activated',
        errorLabel: 'Audience activation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.DEACTIVATE_AUDIENCE]: {
        runningLabel: 'Deactivating audience...',
        completedLabel: 'Audience deactivated',
        errorLabel: 'Audience deactivation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.ARCHIVE_AUDIENCE]: {
        runningLabel: 'Archiving audience...',
        completedLabel: 'Audience archived',
        errorLabel: 'Audience archive failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.RESTORE_AUDIENCE]: {
        runningLabel: 'Restoring audience...',
        completedLabel: 'Audience restored',
        errorLabel: 'Audience restore failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CACHE_AUDIENCE]: {
        runningLabel: 'Caching audience...',
        completedLabel: 'Audience cached',
        errorLabel: 'Audience cache failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.MATERIALIZE_AUDIENCE]: {
        runningLabel: 'Materializing audience...',
        completedLabel: 'Audience materialization started',
        errorLabel: 'Audience materialization failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.EXPORT_AUDIENCE]: {
        runningLabel: 'Exporting audience...',
        completedLabel: 'Audience export started',
        errorLabel: 'Audience export failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.EXPORT_AUDIENCE_SYNC]: {
        runningLabel: 'Starting audience export sync...',
        completedLabel: 'Audience export sync started',
        errorLabel: 'Audience export sync failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.COUNT_AUDIENCE]: {
        runningLabel: 'Counting audience...',
        completedLabel: 'Audience count ready',
        errorLabel: 'Audience count failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_CAMPAIGNS]: {
        runningLabel: 'Fetching campaigns...',
        completedLabel: 'Campaigns loaded',
        errorLabel: 'Failed to fetch campaigns',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_EXTENDED_CAMPAIGN]: {
        runningLabel: 'Fetching campaign details...',
        completedLabel: 'Campaign details loaded',
        errorLabel: 'Failed to fetch campaign details',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_CAMPAIGN_RUNS]: {
        runningLabel: 'Fetching campaign runs...',
        completedLabel: 'Campaign runs loaded',
        errorLabel: 'Failed to fetch campaign runs',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.PUBLISH_CAMPAIGN]: {
        runningLabel: 'Publishing campaign...',
        completedLabel: 'Campaign published',
        errorLabel: 'Campaign publish failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UNPUBLISH_CAMPAIGN]: {
        runningLabel: 'Unpublishing campaign...',
        completedLabel: 'Campaign unpublished',
        errorLabel: 'Campaign unpublish failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.DELETE_CAMPAIGN]: {
        runningLabel: 'Deleting campaign...',
        completedLabel: 'Campaign deleted',
        errorLabel: 'Campaign deletion failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.TRIGGER_CAMPAIGN]: {
        runningLabel: 'Triggering campaign...',
        completedLabel: 'Campaign triggered',
        errorLabel: 'Campaign trigger failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CANCEL_CAMPAIGN]: {
        runningLabel: 'Cancelling campaign...',
        completedLabel: 'Campaign cancelled',
        errorLabel: 'Campaign cancellation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.SEND_TEST_CAMPAIGN]: {
        runningLabel: 'Sending campaign test...',
        completedLabel: 'Campaign test sent',
        errorLabel: 'Campaign test failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_CAMPAIGN_STATUS]: {
        runningLabel: 'Checking campaign status...',
        completedLabel: 'Campaign status loaded',
        errorLabel: 'Campaign status check failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.TRIGGER_INTERNAL_CAMPAIGN_QA]: {
        runningLabel: 'Triggering campaign QA...',
        completedLabel: 'Campaign QA triggered',
        errorLabel: 'Campaign QA trigger failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_INTERNAL_CAMPAIGN_QA]: {
        runningLabel: 'Loading campaign QA...',
        completedLabel: 'Campaign QA loaded',
        errorLabel: 'Campaign QA load failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.SAVE_INTERNAL_CAMPAIGN_QA_REPORT]: {
        runningLabel: 'Saving campaign QA...',
        completedLabel: 'Campaign QA saved',
        errorLabel: 'Campaign QA save failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.DUPLICATE_CAMPAIGN]: {
        runningLabel: 'Duplicating campaign...',
        completedLabel: 'Campaign duplicated',
        errorLabel: 'Campaign duplication failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_RELATION]: {
        runningLabel: 'Fetching relation...',
        completedLabel: 'Relation loaded',
        errorLabel: 'Failed to fetch relation',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_PROJECT]: {
        runningLabel: 'Fetching project...',
        completedLabel: 'Project loaded',
        errorLabel: 'Failed to fetch project',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.BEEFREE_TOOL]: {
        runningLabel: 'Generating template...',
        completedLabel: 'Template generated',
        errorLabel: 'Template generation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.WEB_FETCH_SAFE]: {
        runningLabel: 'Fetching reference content...',
        completedLabel: 'Content fetched',
        errorLabel: 'Failed to fetch content',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_SRT_TEMPLATE]: {
        runningLabel: 'Fetching template from library...',
        completedLabel: 'Template loaded',
        errorLabel: 'Failed to fetch template',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_BEEFREE_TEMPLATE]: {
        runningLabel: 'Fetching community template...',
        completedLabel: 'Template loaded',
        errorLabel: 'Failed to fetch template',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.SAVE_AGENT_TASK_INSIGHT]: {
        runningLabel: 'Saving insight...',
        completedLabel: 'Insight saved',
        errorLabel: 'Failed to save insight',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_INTELLIGENCE_BLOCKS]: {
        runningLabel: 'Fetching intelligence blocks...',
        completedLabel: 'Intelligence blocks loaded',
        errorLabel: 'Failed to fetch intelligence blocks',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CREATE_INTELLIGENCE_BLOCK]: {
        runningLabel: 'Creating intelligence block...',
        completedLabel: 'Intelligence block created',
        errorLabel: 'Failed to create intelligence block',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UPDATE_INTELLIGENCE_BLOCK]: {
        runningLabel: 'Updating intelligence block...',
        completedLabel: 'Intelligence block updated',
        errorLabel: 'Failed to update intelligence block',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.READ_MEMORY]: {
        runningLabel: 'Reading memory...',
        completedLabel: 'Memory read',
        errorLabel: 'Failed to read memory',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.ADD_TO_MEMORY]: {
        runningLabel: 'Updating memory...',
        completedLabel: 'Memory updated',
        errorLabel: 'Failed to update memory',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_CUSTOM_ATTRIBUTES]: {
        runningLabel: 'Fetching custom attributes...',
        completedLabel: 'Custom attributes loaded',
        errorLabel: 'Failed to fetch custom attributes',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_CUSTOM_ATTRIBUTE_INSIGHTS]: {
        runningLabel: 'Checking custom attribute insights...',
        completedLabel: 'Custom attribute insights ready',
        errorLabel: 'Custom attribute insight check failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.PREVIEW_CUSTOM_ATTRIBUTE]: {
        runningLabel: 'Previewing custom attribute...',
        completedLabel: 'Custom attribute preview ready',
        errorLabel: 'Custom attribute preview failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CREATE_CUSTOM_ATTRIBUTE]: {
        runningLabel: 'Creating custom attribute...',
        completedLabel: 'Custom attribute created',
        errorLabel: 'Custom attribute creation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UPDATE_CUSTOM_ATTRIBUTE]: {
        runningLabel: 'Updating custom attribute...',
        completedLabel: 'Custom attribute updated',
        errorLabel: 'Custom attribute update failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.DELETE_CUSTOM_ATTRIBUTE]: {
        runningLabel: 'Deleting custom attribute...',
        completedLabel: 'Custom attribute deleted',
        errorLabel: 'Custom attribute deletion failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.TOGGLE_CUSTOM_ATTRIBUTE_MATERIALIZATION]: {
        runningLabel: 'Updating custom attribute materialization...',
        completedLabel: 'Custom attribute materialization updated',
        errorLabel: 'Custom attribute materialization update failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_KPIS]: {
        runningLabel: 'Fetching KPIs...',
        completedLabel: 'KPIs loaded',
        errorLabel: 'Failed to fetch KPIs',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_KPI]: {
        runningLabel: 'Fetching KPI...',
        completedLabel: 'KPI loaded',
        errorLabel: 'Failed to fetch KPI',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.PREVIEW_KPI]: {
        runningLabel: 'Previewing KPI...',
        completedLabel: 'KPI preview ready',
        errorLabel: 'KPI preview failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CREATE_KPI]: {
        runningLabel: 'Creating KPI...',
        completedLabel: 'KPI created',
        errorLabel: 'KPI creation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UPDATE_KPI]: {
        runningLabel: 'Updating KPI...',
        completedLabel: 'KPI updated',
        errorLabel: 'KPI update failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_KPI_DATA]: {
        runningLabel: 'Fetching KPI data...',
        completedLabel: 'KPI data loaded',
        errorLabel: 'Failed to fetch KPI data',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_KPI_ANALYTICS]: {
        runningLabel: 'Fetching KPI analytics...',
        completedLabel: 'KPI analytics loaded',
        errorLabel: 'Failed to fetch KPI analytics',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.RUN_KPI_MATERIALIZATION]: {
        runningLabel: 'Calculating KPI...',
        completedLabel: 'KPI calculated',
        errorLabel: 'KPI calculation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_ANALYTICS_ARTIFACTS]: {
        runningLabel: 'Fetching analytics artifacts...',
        completedLabel: 'Analytics artifacts loaded',
        errorLabel: 'Failed to fetch analytics artifacts',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_ANALYTICS_ARTIFACT]: {
        runningLabel: 'Fetching analytics artifact...',
        completedLabel: 'Analytics artifact loaded',
        errorLabel: 'Failed to fetch analytics artifact',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_SPACES]: {
        runningLabel: 'Fetching spaces...',
        completedLabel: 'Spaces loaded',
        errorLabel: 'Failed to fetch spaces',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CREATE_SPACE]: {
        runningLabel: 'Creating space...',
        completedLabel: 'Space created',
        errorLabel: 'Space creation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_DASHBOARDS]: {
        runningLabel: 'Fetching dashboards...',
        completedLabel: 'Dashboards loaded',
        errorLabel: 'Failed to fetch dashboards',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_DASHBOARD]: {
        runningLabel: 'Fetching dashboard...',
        completedLabel: 'Dashboard loaded',
        errorLabel: 'Failed to fetch dashboard',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CREATE_CHART]: {
        runningLabel: 'Saving chart...',
        completedLabel: 'Chart saved',
        errorLabel: 'Chart save failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CREATE_DASHBOARD]: {
        runningLabel: 'Creating dashboard...',
        completedLabel: 'Dashboard created',
        errorLabel: 'Dashboard creation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.ADD_CHARTS_TO_DASHBOARD]: {
        runningLabel: 'Adding charts to dashboard...',
        completedLabel: 'Charts added to dashboard',
        errorLabel: 'Failed to add charts to dashboard',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UPDATE_CHART]: {
        runningLabel: 'Updating chart...',
        completedLabel: 'Chart updated',
        errorLabel: 'Chart update failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_INTERNAL_EVENT_TABLES]: {
        runningLabel: 'Fetching event tables...',
        completedLabel: 'Event tables loaded',
        errorLabel: 'Failed to fetch event tables',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UPDATE_RELATION_TABLE_LABEL]: {
        runningLabel: 'Updating table label...',
        completedLabel: 'Table label updated',
        errorLabel: 'Failed to update table label',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UPDATE_RELATION_TABLE_DESCRIPTION]: {
        runningLabel: 'Updating table description...',
        completedLabel: 'Table description updated',
        errorLabel: 'Failed to update table description',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UPDATE_RELATION_COLUMN_DISPLAY_NAME]: {
        runningLabel: 'Updating column display name...',
        completedLabel: 'Column display name updated',
        errorLabel: 'Failed to update column display name',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UPDATE_RELATION_COLUMN_DESCRIPTION]: {
        runningLabel: 'Updating column description...',
        completedLabel: 'Column description updated',
        errorLabel: 'Failed to update column description',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UPDATE_RELATION_COLUMN_AI_USAGE_CONTEXT]: {
        runningLabel: 'Updating column AI context...',
        completedLabel: 'Column AI context updated',
        errorLabel: 'Failed to update column AI context',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UPDATE_RELATION_COLUMN_SYNONYMS]: {
        runningLabel: 'Updating column synonyms...',
        completedLabel: 'Column synonyms updated',
        errorLabel: 'Failed to update column synonyms',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_JOURNEYS]: {
        runningLabel: 'Fetching journeys...',
        completedLabel: 'Journeys loaded',
        errorLabel: 'Failed to fetch journeys',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_JOURNEY]: {
        runningLabel: 'Fetching journey...',
        completedLabel: 'Journey loaded',
        errorLabel: 'Failed to fetch journey',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_JOURNEY_EVENTS]: {
        runningLabel: 'Fetching journey events...',
        completedLabel: 'Journey events loaded',
        errorLabel: 'Failed to fetch journey events',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_JOURNEY_NODE_PARAMS]: {
        runningLabel: 'Fetching journey node parameters...',
        completedLabel: 'Journey node parameters loaded',
        errorLabel: 'Failed to fetch journey node parameters',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CREATE_JOURNEY]: {
        runningLabel: 'Creating journey...',
        completedLabel: 'Journey created',
        errorLabel: 'Journey creation failed',
        action: {
            type: OpenCodeToolActionType.ENTITY_MUTATION,
            entityType: AiEntityType.JOURNEY,
            operation: OpenCodeEntityMutationOperation.CREATE,
            getEntityId: getJourneyEntityId,
        },
    },
    [OpenCodeToolName.UPDATE_JOURNEY]: {
        runningLabel: 'Updating journey...',
        completedLabel: 'Journey updated',
        errorLabel: 'Journey update failed',
        action: {
            type: OpenCodeToolActionType.ENTITY_MUTATION,
            entityType: AiEntityType.JOURNEY,
            operation: OpenCodeEntityMutationOperation.UPDATE,
            getEntityId: getJourneyEntityId,
        },
    },
    [OpenCodeToolName.ADD_JOURNEY_NODE]: {
        runningLabel: 'Adding journey node...',
        completedLabel: 'Journey node added',
        errorLabel: 'Failed to add journey node',
        action: {
            type: OpenCodeToolActionType.ENTITY_MUTATION,
            entityType: AiEntityType.JOURNEY,
            operation: OpenCodeEntityMutationOperation.UPDATE,
            getEntityId: getJourneyEntityId,
        },
    },
    [OpenCodeToolName.ADD_SEND_MESSAGE_NODE]: {
        runningLabel: 'Adding send message node...',
        completedLabel: 'Send message node added',
        errorLabel: 'Failed to add send message node',
        action: {
            type: OpenCodeToolActionType.ENTITY_MUTATION,
            entityType: AiEntityType.JOURNEY,
            operation: OpenCodeEntityMutationOperation.UPDATE,
            getEntityId: getJourneyEntityId,
        },
    },
    [OpenCodeToolName.GET_TABLE]: {
        runningLabel: 'Fetching table schema...',
        completedLabel: 'Table schema loaded',
        errorLabel: 'Failed to fetch table schema',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_RELATION_ID]: {
        runningLabel: 'Fetching relation ID...',
        completedLabel: 'Relation ID loaded',
        errorLabel: 'Failed to fetch relation ID',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CREATE_CAMPAIGN]: {
        runningLabel: 'Creating campaign...',
        completedLabel: 'Campaign created',
        errorLabel: 'Campaign creation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.VALIDATE_CAMPAIGN]: {
        runningLabel: 'Validating campaign...',
        completedLabel: 'Campaign validation completed',
        errorLabel: 'Campaign validation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UPDATE_CAMPAIGN]: {
        runningLabel: 'Updating campaign...',
        completedLabel: 'Campaign updated',
        errorLabel: 'Campaign update failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.SET_CAMPAIGN_SEND_TO]: {
        runningLabel: 'Setting campaign send-to field...',
        completedLabel: 'Campaign send-to field set',
        errorLabel: 'Failed to set campaign send-to field',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.SET_CAMPAIGN_SENDER_DETAILS]: {
        runningLabel: 'Setting campaign sender details...',
        completedLabel: 'Campaign sender details set',
        errorLabel: 'Failed to set campaign sender details',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.SET_CAMPAIGN_COMMUNICATION_DETAILS]: {
        runningLabel: 'Setting campaign communication details...',
        completedLabel: 'Campaign communication details set',
        errorLabel: 'Failed to set campaign communication details',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.SET_CAMPAIGN_SUBSCRIPTION_GROUP]: {
        runningLabel: 'Setting campaign subscription group...',
        completedLabel: 'Campaign subscription group set',
        errorLabel: 'Failed to set campaign subscription group',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.SET_CAMPAIGN_AUDIENCE]: {
        runningLabel: 'Setting campaign audience...',
        completedLabel: 'Campaign audience set',
        errorLabel: 'Failed to set campaign audience',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.SET_CAMPAIGN_CONTENT_MAPPINGS]: {
        runningLabel: 'Setting campaign content mappings...',
        completedLabel: 'Campaign content mappings set',
        errorLabel: 'Failed to set campaign content mappings',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.SET_CAMPAIGN_TRACKING_PARAMETERS]: {
        runningLabel: 'Setting campaign tracking parameters...',
        completedLabel: 'Campaign tracking parameters set',
        errorLabel: 'Failed to set campaign tracking parameters',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.SET_CAMPAIGN_PREVIEW_TEXT]: {
        runningLabel: 'Setting campaign preview text...',
        completedLabel: 'Campaign preview text set',
        errorLabel: 'Failed to set campaign preview text',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.SET_CAMPAIGN_DELIVERY_CONTROLS]: {
        runningLabel: 'Setting campaign delivery controls...',
        completedLabel: 'Campaign delivery controls set',
        errorLabel: 'Failed to set campaign delivery controls',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.SET_CAMPAIGN_CONVERSION_CONFIG]: {
        runningLabel: 'Setting campaign conversion config...',
        completedLabel: 'Campaign conversion config set',
        errorLabel: 'Failed to set campaign conversion config',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_SENDER_PROFILES]: {
        runningLabel: 'Fetching sender profiles...',
        completedLabel: 'Sender profiles loaded',
        errorLabel: 'Failed to fetch sender profiles',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_SUBSCRIPTION_GROUPS]: {
        runningLabel: 'Fetching subscription groups...',
        completedLabel: 'Subscription groups loaded',
        errorLabel: 'Failed to fetch subscription groups',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_TEMPLATES]: {
        runningLabel: 'Fetching templates...',
        completedLabel: 'Templates loaded',
        errorLabel: 'Failed to fetch templates',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_TEMPLATES_FROM_CAMPAIGNS]: {
        runningLabel: 'Fetching campaign templates...',
        completedLabel: 'Campaign templates loaded',
        errorLabel: 'Failed to fetch campaign templates',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_TEMPLATE_DETAILS]: {
        runningLabel: 'Fetching template details...',
        completedLabel: 'Template details loaded',
        errorLabel: 'Failed to fetch template details',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_TEMPLATE_TYPE]: {
        runningLabel: 'Checking template type...',
        completedLabel: 'Template type checked',
        errorLabel: 'Failed to check template type',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_TEMPLATE_THUMBNAIL]: {
        runningLabel: 'Fetching template thumbnail...',
        completedLabel: 'Template thumbnail loaded',
        errorLabel: 'Failed to fetch template thumbnail',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.ESTIMATE_CAMPAIGN_REACH]: {
        runningLabel: 'Estimating campaign reach...',
        completedLabel: 'Campaign reach estimated',
        errorLabel: 'Failed to estimate campaign reach',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_INTEGRATIONS]: {
        runningLabel: 'Fetching integrations...',
        completedLabel: 'Integrations loaded',
        errorLabel: 'Failed to fetch integrations',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.RANDOM_ID_GENERATOR]: {
        runningLabel: 'Generating ID...',
        completedLabel: 'ID generated',
        errorLabel: 'Failed to generate ID',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.ATTACH_TEMPLATE_TO_CAMPAIGN_VARIANT]: {
        runningLabel: 'Attaching template to campaign...',
        completedLabel: 'Template attached',
        errorLabel: 'Failed to attach template',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_PROJECTS]: {
        runningLabel: 'Fetching projects...',
        completedLabel: 'Projects loaded',
        errorLabel: 'Failed to fetch projects',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_USER_SUBSCRIPTION_PREFERENCES]: {
        runningLabel: 'Fetching subscription preferences...',
        completedLabel: 'Subscription preferences loaded',
        errorLabel: 'Failed to fetch subscription preferences',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UPDATE_USER_SUBSCRIPTION_PREFERENCES]: {
        runningLabel: 'Updating subscription preferences...',
        completedLabel: 'Subscription preferences updated',
        errorLabel: 'Failed to update subscription preferences',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_AGENT_TASK]: {
        runningLabel: 'Fetching agent task...',
        completedLabel: 'Agent task loaded',
        errorLabel: 'Failed to fetch agent task',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_AGENT_TASK_LIST]: {
        runningLabel: 'Fetching agent tasks...',
        completedLabel: 'Agent tasks loaded',
        errorLabel: 'Failed to fetch agent tasks',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CREATE_AGENT_TASK]: {
        runningLabel: 'Creating agent task...',
        completedLabel: 'Agent task created',
        errorLabel: 'Agent task creation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UPDATE_AGENT_TASK]: {
        runningLabel: 'Updating agent task...',
        completedLabel: 'Agent task updated',
        errorLabel: 'Agent task update failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.ACTIVATE_AGENT_TASK]: {
        runningLabel: 'Activating agent task...',
        completedLabel: 'Agent task activated',
        errorLabel: 'Agent task activation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.DEACTIVATE_AGENT_TASK]: {
        runningLabel: 'Deactivating agent task...',
        completedLabel: 'Agent task deactivated',
        errorLabel: 'Agent task deactivation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.DELETE_AGENT_TASK]: {
        runningLabel: 'Deleting agent task...',
        completedLabel: 'Agent task deleted',
        errorLabel: 'Agent task deletion failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.RUN_AGENT_TASK_NOW]: {
        runningLabel: 'Running agent task...',
        completedLabel: 'Agent task run started',
        errorLabel: 'Agent task run failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_AGENT_TASK_INSIGHTS]: {
        runningLabel: 'Fetching agent task insights...',
        completedLabel: 'Agent task insights loaded',
        errorLabel: 'Failed to fetch agent task insights',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_AGENT_TASK_INSIGHT]: {
        runningLabel: 'Fetching agent task insight...',
        completedLabel: 'Agent task insight loaded',
        errorLabel: 'Failed to fetch agent task insight',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UPDATE_AGENT_TASK_INSIGHT]: {
        runningLabel: 'Updating agent task insight...',
        completedLabel: 'Agent task insight updated',
        errorLabel: 'Agent task insight update failed',
        action: {
            type: OpenCodeToolActionType.ENTITY_MUTATION,
            entityType: AiEntityType.AGENT_TASK,
            operation: OpenCodeEntityMutationOperation.UPDATE,
            getEntityId: (output) =>
                typeof output.taskId === 'string' ? output.taskId : undefined,
        },
    },
    [OpenCodeToolName.LIST_QUESTS]: {
        runningLabel: 'Loading quests...',
        completedLabel: 'Quests loaded',
        errorLabel: 'Failed to load quests',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CREATE_QUEST]: {
        runningLabel: 'Creating quest...',
        completedLabel: 'Quest created',
        errorLabel: 'Quest creation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_QUEST]: {
        runningLabel: 'Loading quest...',
        completedLabel: 'Quest loaded',
        errorLabel: 'Failed to load quest',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_QUEST_TASKS]: {
        runningLabel: 'Loading quest tasks...',
        completedLabel: 'Quest tasks loaded',
        errorLabel: 'Failed to load quest tasks',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_QUEST_ACTIVE_WAIT]: {
        runningLabel: 'Loading quest wait...',
        completedLabel: 'Quest wait loaded',
        errorLabel: 'Failed to load quest wait',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_QUEST_HANDOFFS]: {
        runningLabel: 'Loading quest handoffs...',
        completedLabel: 'Quest handoffs loaded',
        errorLabel: 'Failed to load quest handoffs',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.STEER_QUEST]: {
        runningLabel: 'Steering quest...',
        completedLabel: 'Quest steered',
        errorLabel: 'Quest steering failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.START_QUEST_CHAT]: {
        runningLabel: 'Starting quest chat...',
        completedLabel: 'Quest chat started',
        errorLabel: 'Quest chat start failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.ADD_QUEST_PLAN_ITEM]: {
        runningLabel: 'Adding quest plan item...',
        completedLabel: 'Quest plan item added',
        errorLabel: 'Quest plan item add failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UPDATE_QUEST_PLAN_ITEM]: {
        runningLabel: 'Updating quest plan item...',
        completedLabel: 'Quest plan item updated',
        errorLabel: 'Quest plan item update failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.MOVE_QUEST_PLAN_ITEM]: {
        runningLabel: 'Moving quest plan item...',
        completedLabel: 'Quest plan item moved',
        errorLabel: 'Quest plan item move failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.DELETE_QUEST_PLAN_ITEM]: {
        runningLabel: 'Deleting quest plan item...',
        completedLabel: 'Quest plan item deleted',
        errorLabel: 'Quest plan item delete failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.COMPLETE_QUEST]: {
        runningLabel: 'Completing quest...',
        completedLabel: 'Quest completed',
        errorLabel: 'Quest completion failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CREATE_QUEST_HUMAN_INPUT]: {
        runningLabel: 'Creating quest question...',
        completedLabel: 'Quest question created',
        errorLabel: 'Quest question creation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CANCEL_QUEST_HUMAN_INPUT]: {
        runningLabel: 'Cancelling quest question...',
        completedLabel: 'Quest question cancelled',
        errorLabel: 'Quest question cancellation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.SUPERSEDE_QUEST_HUMAN_INPUT]: {
        runningLabel: 'Superseding quest question...',
        completedLabel: 'Quest question superseded',
        errorLabel: 'Quest question supersede failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_QUEST_HUMAN_INPUTS]: {
        runningLabel: 'Loading quest questions...',
        completedLabel: 'Quest questions loaded',
        errorLabel: 'Failed to load quest questions',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.APPEND_QUEST_ACTIVITY_LOG]: {
        runningLabel: 'Recording quest activity...',
        completedLabel: 'Quest activity recorded',
        errorLabel: 'Failed to record quest activity',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_QUEST_ACTIVITY_LOG]: {
        runningLabel: 'Loading quest activity...',
        completedLabel: 'Quest activity loaded',
        errorLabel: 'Failed to load quest activity',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.SEND_QUEST_HANDOFF]: {
        runningLabel: 'Sending quest handoff...',
        completedLabel: 'Quest handoff sent',
        errorLabel: 'Quest handoff failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.WAIT_UNTIL_QUEST_TASKS]: {
        runningLabel: 'Scheduling quest wake-up...',
        completedLabel: 'Quest wake-up scheduled',
        errorLabel: 'Quest wake-up scheduling failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UPDATE_QUEST_REPORT]: {
        runningLabel: 'Updating quest report...',
        completedLabel: 'Quest report updated',
        errorLabel: 'Quest report update failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.WEB_SEARCH]: {
        runningLabel: 'Searching the web...',
        completedLabel: 'Web search complete',
        errorLabel: 'Web search failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_HOME_FEED_ITEMS]: {
        runningLabel: 'Fetching home feed items...',
        completedLabel: 'Home feed items loaded',
        errorLabel: 'Failed to fetch home feed items',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CREATE_HOME_FEED_ITEM]: {
        runningLabel: 'Creating home feed item...',
        completedLabel: 'Home feed item created',
        errorLabel: 'Home feed item creation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_HOME_FEED_ITEM]: {
        runningLabel: 'Fetching home feed item...',
        completedLabel: 'Home feed item loaded',
        errorLabel: 'Failed to fetch home feed item',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UPDATE_HOME_FEED_ITEM]: {
        runningLabel: 'Updating home feed item...',
        completedLabel: 'Home feed item updated',
        errorLabel: 'Home feed item update failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.MARK_HOME_FEED_ITEM_READ]: {
        runningLabel: 'Updating home feed read state...',
        completedLabel: 'Home feed read state updated',
        errorLabel: 'Home feed read state update failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.DELETE_HOME_FEED_ITEM]: {
        runningLabel: 'Deleting home feed item...',
        completedLabel: 'Home feed item deleted',
        errorLabel: 'Home feed item deletion failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CREATE_ENTITY_TAG]: {
        runningLabel: 'Adding reference...',
        completedLabel: 'Reference added',
        errorLabel: 'Reference failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CREATE_CHART_TAG]: {
        runningLabel: 'Preparing chart...',
        completedLabel: 'Chart ready',
        errorLabel: 'Chart failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CREATE_SQL_TAG]: {
        runningLabel: 'Preparing query details...',
        completedLabel: 'Query details ready',
        errorLabel: 'Query details failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CONNECTOR_API_REQUEST]: {
        runningLabel: 'Requesting connector data...',
        completedLabel: 'Connector data loaded',
        errorLabel: 'Connector request failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CREATE_CUSTOM_ATTRIBUTE_SYNC]: {
        runningLabel: 'Creating custom attribute sync...',
        completedLabel: 'Custom attribute sync created',
        errorLabel: 'Custom attribute sync creation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CREATE_AUDIENCE_DESTINATION_SYNC]: {
        runningLabel: 'Creating audience sync...',
        completedLabel: 'Audience sync created',
        errorLabel: 'Audience sync creation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.BEEFREE_V2_CREATE_TEMPLATE]: {
        runningLabel: 'Creating email template...',
        completedLabel: 'Email template created',
        errorLabel: 'Template creation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.BEEFREE_V2_GET_TEMPLATE]: {
        runningLabel: 'Retrieving email template...',
        completedLabel: 'Email template retrieved',
        errorLabel: 'Template retrieval failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.CREATE_PUSH_TEMPLATE]: {
        runningLabel: 'Creating push notification template...',
        completedLabel: 'Push notification template created',
        errorLabel: 'Push notification template creation failed',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.READ_ATTACHMENT]: {
        runningLabel: 'Reading attachment...',
        completedLabel: 'Attachment loaded',
        errorLabel: 'Failed to read attachment',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_PUBLIC_ATTACHMENT_URL]: {
        runningLabel: 'Generating public URL for attachment...',
        completedLabel: 'Public attachment URL ready',
        errorLabel: 'Failed to generate public attachment URL',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UPLOAD_FILE_TO_RELATION]: {
        runningLabel: 'Uploading file to relation...',
        completedLabel: 'File uploaded to relation',
        errorLabel: 'Failed to upload file to relation',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.UPSERT_TABLE_FILE_TO_RELATION]: {
        runningLabel: 'Upserting table file to relation...',
        completedLabel: 'Table file upserted to relation',
        errorLabel: 'Failed to upsert table file to relation',
        action: NONE_ACTION,
    },
    [OpenCodeToolName.GET_FILE_UPLOAD_STATS]: {
        runningLabel: 'Fetching file upload stats...',
        completedLabel: 'File upload stats retrieved',
        errorLabel: 'Failed to fetch file upload stats',
        action: NONE_ACTION,
    },
};

const DEFAULT_TOOL_CONFIG: OpenCodeToolDisplayConfig = {
    runningLabel: 'Processing...',
    completedLabel: 'Done',
    errorLabel: 'Failed',
    action: NONE_ACTION,
};

const BEEFREE_V2_TOOL_PREFIX = 'beefree-v2_';
const BEEFREE_TOOL_NAME_PREFIX = 'beefree_';
const BEEFREE_V2_TOOL_NAME_PREFIX = 'beefree_v2_';

const toSentenceCase = (value: string): string => {
    if (!value) {
        return value;
    }

    return `${value[0].toUpperCase()}${value.slice(1)}`;
};

const getBeefreeV2ToolLabel = (toolName: string): string | undefined => {
    if (!toolName.startsWith(BEEFREE_V2_TOOL_PREFIX)) {
        return undefined;
    }

    const unprefixedToolName = toolName.slice(BEEFREE_V2_TOOL_PREFIX.length);
    const readableToolName = unprefixedToolName
        .replace(BEEFREE_V2_TOOL_NAME_PREFIX, '')
        .replace(BEEFREE_TOOL_NAME_PREFIX, '')
        .split('_')
        .filter(Boolean)
        .join(' ');

    return toSentenceCase(readableToolName);
};

const getBeefreeV2ToolConfig = (
    toolName: string,
): OpenCodeToolDisplayConfig | undefined => {
    const label = getBeefreeV2ToolLabel(toolName);
    if (!label) {
        return undefined;
    }

    return {
        runningLabel: `${label}...`,
        completedLabel: label,
        errorLabel: `${label} failed`,
        action: NONE_ACTION,
    };
};

export function getOpenCodeToolConfig(
    toolName: string,
): OpenCodeToolDisplayConfig {
    return (
        OPEN_CODE_TOOL_DISPLAY_CONFIG[toolName] ??
        getBeefreeV2ToolConfig(toolName) ??
        DEFAULT_TOOL_CONFIG
    );
}
